import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { CardReplaceRequest, TenantAddonView } from '@/api/generated';
import { CardCapture } from '@/features/billing/CardCapture';
import { isCardDeclinedCode } from '@/features/billing/cardDeclined';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { useActionError, type ActionError } from '@/components/useActionError';

export interface UpdateCardDialogProps {
  addon: TenantAddonView | null;
  onClose: () => void;
  onReplace: (id: number, body: CardReplaceRequest) => Promise<void>;
}

export function UpdateCardDialog({ addon, onClose, onReplace }: UpdateCardDialogProps) {
  const { t } = useTranslation();
  const describe = useActionError();
  const [submitting, setSubmitting] = useState(false);
  // The replace's failure, with its Reference when the API sent one (TMMRSL-0072); the card's own
  // Stripe messages stay in the card capture.
  const [error, setError] = useState<ActionError | null>(null);

  useEffect(() => {
    if (!addon) return;
    setError(null);
  }, [addon]);

  function handleClose() {
    if (submitting) return;
    onClose();
  }

  async function handleToken(token: string): Promise<void | 'retry'> {
    if (!addon) return;
    setSubmitting(true);
    setError(null);
    try {
      await onReplace(addon.id, { cardToken: token });
      onClose();
    } catch (err) {
      setError(describe(err));
      if (isCardDeclinedCode(err)) return 'retry';
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={addon !== null} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('addons.updateCardTitle', { name: addon ? addon.name : '' })}</DialogTitle>
      <DialogContent>
        <CardCapture scope="platform"
          submitLabel={t('addons.updateCardConfirm')}
          busy={submitting}
          onToken={handleToken}
        />
        <ActionErrorAlert error={error} sx={{ mt: 2 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
